import { createContext, Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { Api } from "../general/api";
import { GetMyModulesApiResponseDto, isFinishedEvent, LearningPathDto, MessageDto, ModuleStatusEnum, MyModuleDto, SessionDto } from "../general/dtos";
import { useApi } from "./api-provider";

export interface UserHomeDataContextValue {
    isAuthenticated: boolean;
    setIsAuthenticated: Dispatch<SetStateAction<boolean>>;

    // Learning Path
    learningPath: LearningPathDto;
    learningPathLoading: boolean;
    learningPathError: string | null;
    loadLearningPath: () => Promise<void>;

    // all user modules
    allUserModules: MyModuleDto[];
    allUserModulesLoading: boolean;
    allUserModulesError: string | null;
    loadAllUserModules: () => Promise<void>;

    // full module including content
    fullModule: MyModuleDto;
    fullModuleLoading: boolean;
    fullModuleError: string | null;
    loadFullModule: (moduleKey: string, startSession: boolean) => Promise<MyModuleDto | null>;

    // session support
    session: SessionDto | undefined;
    isModuleCompleted: boolean;
    endSession: () => Promise<SessionDto | null>;
    disregardSession: () => Promise<void>;
    createSessionEvent: (payload: string) => Promise<void>;
}

export const UserHomeDataContext = createContext<UserHomeDataContextValue | null>(null);

export type UserHomeDataProviderProps = {
  children: React.ReactNode;
  forImpersonation?: boolean;
  impersonatedUserId?: string; // dynamic, changes as admin navigates
};


export function UserHomeDataProvider({ children, forImpersonation, impersonatedUserId }: UserHomeDataProviderProps) {
  const api: Api = useApi();

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  // Learning Path
  const [learningPath, setLearningPath] = useState<LearningPathDto>({} as LearningPathDto);
  const [learningPathLoading, setLearningPathLoading] = useState<boolean>(false);
  const [learningPathError, setLearningPathError] = useState<string | null>(null);
  const learningPathInFlight = useRef<Promise<void> | null>(null);

  // all user modules
  const [allUserModules, setAllUserModules] = useState<MyModuleDto[]>([]);
  const [allUserModulesLoading, setAllUserModulesLoading] = useState<boolean>(false);
  const [allUserModulesError, setAllUserModulesError] = useState<string | null>(null);
  const allUserModulesInFlight = useRef<Promise<void> | null>(null);

  // full module including content
  const [fullModule, setFullModule] = useState<MyModuleDto>({} as MyModuleDto);
  const [fullModuleLoading, setFullModuleLoading] = useState<boolean>(false);
  const [fullModuleError, setFullModuleError] = useState<string | null>(null);
  const fullModulesInFlight = useRef<Record<string, Promise<MyModuleDto | null> | null>>({});
  const moduleLoadGeneration = useRef(0);
  useEffect(() => () => {
    moduleLoadGeneration.current++;
    fullModulesInFlight.current = {};
  }, []);

  // session support
  const [session, setSession] = useState<SessionDto | undefined>(undefined);
  const [isModuleCompleted, setIsModuleCompleted] = useState<boolean>(false);

  const effectiveUserKey = (): string => {
    if (forImpersonation) {
        return impersonatedUserId ?? 'non existing user key';
    }
    return '';
  }  

  const loadLearningPath = async (): Promise<void> => {
    if (learningPathInFlight.current) {
        return learningPathInFlight.current;
    }

    setLearningPathLoading(true);
    setLearningPathError(null);

    const promise = api.getUserTodoList(effectiveUserKey())
    .then((data: LearningPathDto | MessageDto) => {
        if (data && !data.hasOwnProperty('message')) {
            setLearningPath(data as LearningPathDto);
            setLearningPathLoading(false);
            learningPathInFlight.current = null;
        } else {
            const message: string = (data as MessageDto).message;
            setLearningPathError(message);
        }
    })
    .catch((error) => {
      const message: string = error.response?.data?.message ?? 'Load error';
      setLearningPathError(message);
      setLearningPathLoading(false);
      learningPathInFlight.current = null;
    });

    learningPathInFlight.current = promise;
    return promise;
  };

  const loadAllUserModules = async (): Promise<void> => {
    if (allUserModulesInFlight.current) {
        return allUserModulesInFlight.current;
    }

    setAllUserModulesLoading(true);
    setAllUserModulesError(null);

    //const promise = api.getMyModules(1, 1000, '', '', '', '')
    const promise = (forImpersonation ? api.getAllUserModules(effectiveUserKey(), 1, 1000, '', '', '', '') : api.getMyModules(1, 1000, '', '', '', ''))
    .then((data: GetMyModulesApiResponseDto) => {
        setAllUserModules(data.data);
        setAllUserModulesLoading(false);
        allUserModulesInFlight.current = null;
    })
    .catch((error) => {
      const message: string = error.response?.data?.message ?? 'Load error';
      setAllUserModulesError(message);
      setAllUserModulesLoading(false);
      allUserModulesInFlight.current = null;
    });

    allUserModulesInFlight.current = promise;
    return promise;
  };

  const loadFullModule = async (moduleKey: string, startSession: boolean): Promise<MyModuleDto | null> => {
    if (forImpersonation) return null;

    if (fullModulesInFlight.current[moduleKey]) {
        return fullModulesInFlight.current[moduleKey]!;
    }

    setFullModuleLoading(true);
    setFullModuleError(null);
    if (startSession) setSession(undefined);
    const generation = ++moduleLoadGeneration.current;

    const promise: Promise<MyModuleDto | null> = api.getMyModule(moduleKey)
      .then(async (data) => {
        if (generation !== moduleLoadGeneration.current) return null;
        if (!data?.module_key) {
            setFullModuleLoading(false);
            return null;
        }
        if (startSession) {
            // The viewer must not open before its events and completion can be recorded.
            const startedSession = await api.startSession(moduleKey);
            if (generation !== moduleLoadGeneration.current) return null;
            if (!startedSession?.session_id) throw new Error('Could not start the lesson. Please try again.');
            setSession(startedSession);
        }
        setFullModule(data as MyModuleDto);
        setFullModuleLoading(false);
        fullModulesInFlight.current[moduleKey] = null;
        const completed: boolean = (data as MyModuleDto).status === ModuleStatusEnum.Finished;
        setIsModuleCompleted(completed);
        return data;
      })
      .catch((error) => {
        if (generation !== moduleLoadGeneration.current) return null;
        const message: string = error.response?.data?.message ?? 'Load error';
        setFullModuleError(message);
        setFullModuleLoading(false);
        fullModulesInFlight.current[moduleKey] = null;
        return null;
      }).finally(() => {
        if (fullModulesInFlight.current[moduleKey] === promise) fullModulesInFlight.current[moduleKey] = null;
      });

    fullModulesInFlight.current[moduleKey] = promise;
    return promise;
  };

  const endSession = async (): Promise<SessionDto | null> => {
    if (session) {
        const endingSession = session;
        return api.endSession(session.session_id)
        .then((result) => {
            if (!result?.finished_at) return null;
            setSession(current => current?.session_id === endingSession.session_id ? undefined : current);
            if (isAuthenticated) { void loadLearningPath(); void loadAllUserModules(); }
            return result;
        })
        .catch((e) => {
            console.log(e);
            return null;
        });
    }
    return null;
  }

  const disregardSession = async (): Promise<void> => {
    moduleLoadGeneration.current++;
    fullModulesInFlight.current = {};
    setSession(undefined);
    setFullModuleLoading(false);
  }

  const createSessionEvent = async (payload: string): Promise<void> => {
    if (session) {
        api.createSessionEvent(session.session_id, payload)
        .then((data) => {
            if (data) {
                if (isFinishedEvent(payload)) {
                    setIsModuleCompleted(true);
                }
            }
        })
        .catch((e) => {
            console.log(e);
        });
    }
  }

  useEffect(() => {
    const doLoadData: boolean = forImpersonation ? (impersonatedUserId ?? '').length > 0 : isAuthenticated;
    if (doLoadData) {
        loadLearningPath();
        loadAllUserModules();
    } else {
        setLearningPath({} as LearningPathDto);
        setAllUserModules([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, impersonatedUserId]);

  useEffect(() => {
    const doLoadData: boolean = forImpersonation ? (impersonatedUserId ?? '').length > 0 : isAuthenticated;
    if (doLoadData) {
        loadLearningPath();
        loadAllUserModules();
    }
}, []);
 
  return (
    <UserHomeDataContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,

        learningPath,
        learningPathLoading,
        learningPathError,
        loadLearningPath,

        allUserModules,
        allUserModulesLoading,
        allUserModulesError,
        loadAllUserModules,

        fullModule,
        fullModuleLoading,
        fullModuleError,
        loadFullModule,

        session,
        isModuleCompleted,
        endSession,
        disregardSession,
        createSessionEvent,
      }}
    >
      {children}
    </UserHomeDataContext.Provider>
  );
}
